import { useCallback, useRef } from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { useActiveTheme } from '../../theme/ThemeContext';
import { parseAutoCompleteValue } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { resolveFieldValue } from '../useFieldState';
import AutoComplete, {
  type ISuggestionItem,
} from '../../AutoComplete/AutoComplete';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';

export const AutoCompleteField = ({
  field,
  fieldValue,
  isFieldDisabled,
  validationMessage,
}: FieldComponentProps) => {
  const {
    params,
    handleOtherInfoChange,
    otherInfoObj,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
  } = useOtherInfoContext();
  const theme = useActiveTheme();

  const { metadata, onFetchAutoComplete } = params;

  const dependeeValue = field.dependees
    ? resolveFieldValue(
        field.dependees,
        otherInfoObj,
        metadata.fields,
        getRepeatingPanelIndex,
        currentElementInEntityRepeatingPanel
      )
    : undefined;

  // Use refs so handleFetch always reads latest values without re-creating the callback
  const otherInfoObjRef = useRef(otherInfoObj);
  otherInfoObjRef.current = otherInfoObj;
  const getRepeatingPanelIndexRef = useRef(getRepeatingPanelIndex);
  getRepeatingPanelIndexRef.current = getRepeatingPanelIndex;
  const entityRepeatingPanelRef = useRef(currentElementInEntityRepeatingPanel);
  entityRepeatingPanelRef.current = currentElementInEntityRepeatingPanel;

  const handleFetch = useCallback(
    (
      searchText: string,
      abortController: AbortController
    ): Promise<ISuggestionItem[]> => {
      if (!onFetchAutoComplete) return Promise.resolve([]);

      // Derive dependee value from otherInfo (the single source of truth)
      const dependeeFieldName = field.dependees;
      let dependeesParam: string | undefined;
      if (dependeeFieldName) {
        const dependeeField = metadata.fields.find(
          (f) => f.name === dependeeFieldName
        );
        const rawValue = resolveFieldValue(
          dependeeFieldName,
          otherInfoObjRef.current,
          metadata.fields,
          getRepeatingPanelIndexRef.current,
          entityRepeatingPanelRef.current
        );
        const dependeeKey =
          dependeeField?.fieldType === 'AutoComplete'
            ? parseAutoCompleteValue(rawValue)?.key
            : rawValue != null
            ? String(rawValue)
            : undefined;
        dependeesParam = `${dependeeField?.reference}=${dependeeKey}`;
      }

      return onFetchAutoComplete(
        abortController,
        field.reference ?? '',
        searchText,
        dependeesParam
      ).then((data) => data.map((kv) => ({ key: kv.key, text: kv.value })));
    },
    [field.dependees, field.reference, metadata.fields, onFetchAutoComplete]
  );

  const handleSelect = useCallback(
    (suggestion: ISuggestionItem) => {
      handleOtherInfoChange(field.name, `${suggestion.key}:${suggestion.text}`);
    },
    [field.name, handleOtherInfoChange]
  );

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <FluentProvider theme={theme} style={{ width: '100%' }}>
        <AutoComplete
          key={`${field.name}-${dependeeValue ?? ''}`}
          id={field.name}
          placeholder={field.placeholder}
          value={parseAutoCompleteValue(fieldValue)?.display ?? ''}
          onFetch={handleFetch}
          onSelect={handleSelect}
          disabled={isFieldDisabled}
          style={{ width: '100%' }}
        />
      </FluentProvider>
    </FieldWrapper>
  );
};
